import React, { ReactElement, MouseEvent } from 'react';
import { useRouter } from 'next/router';
import { Icons, Text, Icon } from '@feast-it/ui/light';
import {
  FORMATTER_MINIMUM_FRACTION_DIGITS,
  formatToCurrency,
} from '@feast-it/currency';

import { createSupplierClickHandler } from '@/helpers/analytics';
import {
  SUPPLIER_PROFILE_CLICKED,
  SUPPLIER_REVIEW_CLICKED,
} from '../../constants/analytics';
import {
  BasePriceText,
  Card,
  Container,
  DescriptionWrapper,
  Header,
  ImageContainer,
  QuickResponseWrapper,
  RatingsReviewsWrapper,
  RatingLocationWrapper,
  Wrapper,
  RatingBlock,
  LocationBlock,
  StyledLink,
  RatingStar,
  SupplierTitle,
} from './SupplierCard.styled';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';
import { getSupplierA11y } from '@/helpers/supplierA11y';

export interface SupplierCardProps {
  name: string;
  uuid: string;
  headingAs: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  category?: string;
  images?: ReactElement;
  description?: ReactElement;
  // eslint-disable-next-line no-unused-vars
  onClickLink?: (event: MouseEvent<HTMLAnchorElement>) => void;
  rating?: string;
  numOfReviews?: number;
  basePrice?: number;
  icon?: keyof Icons;
  href: string;
  respondsQuickly?: boolean;
}

export function SupplierCard(props: SupplierCardProps) {
  const { numOfReviews, basePrice, respondsQuickly, onClickLink } = props;
  const router = useRouter();

  const baseConfig = {
    supplierName: props.name,
    href: props.href,
    currentPage: router.asPath,
    source: 'top_supplier',
    onClickLink,
  };

  const handleCardClick = createSupplierClickHandler(
    baseConfig,
    SUPPLIER_PROFILE_CLICKED
  );

  const handleReviewsClick = createSupplierClickHandler(
    {
      ...baseConfig,
      preventDefault: true,
      stopPropagation: true,
      useWindowLocation: true,
    },
    SUPPLIER_REVIEW_CLICKED
  );

  const price =
    typeof basePrice === 'number'
      ? formatToCurrency(basePrice, {
          formatMinimumFraction: FORMATTER_MINIMUM_FRACTION_DIGITS.WHOLE_POUNDS,
        })
      : null;

  const a11y = getSupplierA11y({
    uuid: props.uuid,
    hasRating: props.rating != null,
    hasPrice: !!price,
    respondsQuickly: !!respondsQuickly,
  });

  return (
    <Card>
      <StyledLink
        href={props.href}
        onClick={handleCardClick}
        aria-labelledby={a11y.labelledBy}
      >
        <Container>
          {props.images && (
            <ImageContainer>
              {props.images}

              {respondsQuickly && (
                <QuickResponseWrapper>
                  <Icon name="Clock" size={20} fill="mediumBlue" aria-hidden />

                  <Text id={a11y.quickId} style="bodySmall" color="mediumBlue">
                    Responds quickly
                  </Text>
                </QuickResponseWrapper>
              )}

              {price && (
                <BasePriceText id={a11y.priceId} style="bodyMedium">
                  From {price} per head
                </BasePriceText>
              )}
            </ImageContainer>
          )}

          <Wrapper>
            <Header>
              {props.name && (
                <SupplierTitle
                  as={props.headingAs}
                  style="headingSmall"
                  color="black"
                  id={a11y.headingId}
                >
                  {props.name}
                </SupplierTitle>
              )}

              <RatingLocationWrapper>
                {props.rating && (
                  <RatingBlock>
                    <RatingsReviewsWrapper onClick={handleReviewsClick}>
                      <Text
                        id={a11y.ratingId}
                        style="bodyBoldMedium"
                        as="span"
                        color="black"
                      >
                        <RatingStar aria-hidden>★&nbsp;</RatingStar>

                        <VisuallyHidden>Rated </VisuallyHidden>

                        {props.rating}

                        {numOfReviews > 0 && (
                          <>
                            &nbsp;
                            <Text style="bodyMedium" as="span" color="black">
                              ({numOfReviews} review
                              {numOfReviews > 1 ? 's' : ''})
                            </Text>
                            <VisuallyHidden> in total</VisuallyHidden>
                          </>
                        )}
                      </Text>
                    </RatingsReviewsWrapper>

                    {props.category && (
                      <Text
                        style="bodyMedium"
                        as="span"
                        color="black"
                        aria-hidden
                      >
                        &nbsp;|&nbsp;
                      </Text>
                    )}
                  </RatingBlock>
                )}

                {props.category && (
                  <LocationBlock>{props.category}</LocationBlock>
                )}
              </RatingLocationWrapper>
            </Header>

            {props.description && (
              <DescriptionWrapper>{props.description}</DescriptionWrapper>
            )}
          </Wrapper>
        </Container>
      </StyledLink>
    </Card>
  );
}
